import React, { useMemo, useState } from "react";
import { Row, Col, Form, Button, Card, Alert, Badge, Accordion } from "react-bootstrap";
import { FaPlus, FaExclamationTriangle } from "react-icons/fa";

import {
  Counter,
  SingleSelect,
  MultiSelect,
  StepHeader,
  Section,
  SectionDivider,
  StickyActionBar,
  HelpAndFloorsStep,
  bucketToKg,
  mapServiceToHelpCarry,
  dictByAreaItem,
  dictByKey,
} from "./Wizardshared";
import "./Specialitemswizard.css";

import {
  FURNITURE_AREAS,
  HEAVY_ITEMS,
  ASSEMBLY_SERVICE_OPTIONS,
  ASSEMBLY_LEVEL_OPTIONS,
  KITCHEN_SERVICE_OPTIONS,
  KITCHEN_SIZE_OPTIONS,
  APPLIANCE_CONNECTION_OPTIONS,
  APPLIANCE_CONNECTION_ITEMS,
} from "../config/Furniturewizardconfig";

import {
  BUILDING_MATERIALS,
  PACKAGE_WEIGHT_BUCKETS,
  BULK_PACKAGING_OPTIONS,
  BULK_WEIGHT_BUCKETS,
  TILE_PACKAGING_OPTIONS,
  TILE_WEIGHT_BUCKETS,
  SLAB_PACKAGING_OPTIONS,
  SLAB_SIZE_BUCKETS,
  SLAB_WEIGHT_BUCKETS,
  BRICK_PACKAGING_OPTIONS,
  BRICK_WEIGHT_BUCKETS,
  SHEET_LENGTH_BUCKETS,
  LENGTH_BUCKETS,
  LENGTH_WEIGHT_BUCKETS,
  GLAZED_OPTIONS,
  DOOR_WINDOW_SIZE_OPTIONS,
  PACKAGED_OPTIONS,
  MACHINE_TYPES,
  MACHINE_WEIGHT_BUCKETS,
  CUSTOM_MATERIAL_WEIGHT_BUCKETS,
  TOTAL_WEIGHT_BUCKETS,
  PACKAGING_METHODS,
  PALLET_TYPES,
  PALLET_WEIGHT_BUCKETS,
  LOCATION_TYPES,
  CARRYING_DISTANCE_BUCKETS,
  VEHICLE_ACCESS_DISTANCE_BUCKETS,
  LOADING_SERVICE_OPTIONS,
  HELPERS_OPTIONS,
  SPECIAL_EQUIPMENT_OPTIONS,
  EQUIPMENT_NEEDS_PROVIDER,
  EQUIPMENT_PROVIDER_OPTIONS,
  DEMONTAGE_ANSWER_OPTIONS,
  DEMONTAGE_SERVICE_OPTIONS,
} from "../config/Buildingmaterialswizardconfig";

import {
  TIRE_TYPES,
  ON_RIMS_OPTIONS,
  TIRE_LOCATIONS,
  LOADING_HELP_OPTIONS,
} from "../config/Tireswizardconfig";

import MoveWizard from "./Movewizard";
import { useTranslation } from "react-i18next";

/* ----------------------------------------------------------------------- */
/* FURNITURE (Möbel / Einzelne Gegenstände) — one continuous page          */
/* ----------------------------------------------------------------------- */

const CustomAreaItems = ({ areaKey, items, setItemField }) => {
  const [name, setName] = useState("");
  const existing = Object.values(items).filter((i) => i.area === areaKey);

  const add = () => {
    if (!name.trim()) return;
    setItemField(areaKey, name.trim(), "quantity", 1);
    setName("");
  };

  return (
    <div>
      {existing.map((it) => (
        <div key={it.item} className="mb-2 pb-2 border-bottom">
          <Row className="align-items-center">
            <Col xs={7} sm={5}>
              {it.item}
            </Col>
            <Col xs={5} sm={3}>
              <Counter value={it.quantity} onChange={(v) => setItemField(areaKey, it.item, "quantity", v)} />
            </Col>
          </Row>
          <div className="small text-muted mt-1 mb-1">Größe</div>
          <SingleSelect
            name={`${it.item}-custom-size`}
            options={["Klein", "Mittel", "Groß", "Nicht bekannt"]}
            value={it.size}
            onChange={(v) => setItemField(areaKey, it.item, "size", v)}
          />
        </div>
      ))}
      <Row className="align-items-center">
        <Col xs={8}>
          <Form.Control placeholder="Bezeichnung des Gegenstands" value={name} onChange={(e) => setName(e.target.value)} />
        </Col>
        <Col xs={4}>
          <Button variant="outline-primary" onClick={add}>
            <FaPlus className="me-1" /> Hinzufügen
          </Button>
        </Col>
      </Row>
    </div>
  );
};

const ItemsAccordion = ({ areaDefs, items, setItemField, areaItemCount }) => (
  <Accordion defaultActiveKey={areaDefs[0]?.key} alwaysOpen>
    {areaDefs.map((area) => (
      <Accordion.Item eventKey={area.key} key={area.key}>
        <Accordion.Header>
          <span className="d-flex align-items-center gap-2">
            {area.key}
            {areaItemCount(area) > 0 && <Badge bg="primary">{areaItemCount(area)}</Badge>}
          </span>
        </Accordion.Header>
        <Accordion.Body className="overflow-auto">
          {area.isCustomArea ? (
            <CustomAreaItems areaKey={area.key} items={items} setItemField={setItemField} />
          ) : (
            area.items.map((it) => {
              const k = `${area.key}::${it.key}`;
              const current = items[k] || { quantity: 0 };
              return (
                <div key={k} className="mb-2 pb-2 border-bottom">
                  <Row className="align-items-center">
                    <Col xs={7} sm={5}>
                      {it.key}
                    </Col>
                    <Col xs={5} sm={3}>
                      <Counter value={current.quantity} onChange={(v) => setItemField(area.key, it.key, "quantity", v)} />
                    </Col>
                  </Row>
                  {Number(current.quantity) > 0 && it.typeOptions && (
                    <div className="mt-2">
                      <div className="small text-muted mb-1">Art</div>
                      <SingleSelect
                        name={`${k}-type`}
                        options={it.typeOptions}
                        value={current.type}
                        onChange={(v) => setItemField(area.key, it.key, "type", v)}
                      />
                    </div>
                  )}
                  {Number(current.quantity) > 0 && it.sizeOptions && (
                    <div className="mt-2">
                      <div className="small text-muted mb-1">{it.sizeLabel === "weight" ? "Gewicht" : "Größe"}</div>
                      <SingleSelect
                        name={`${k}-size`}
                        options={it.sizeOptions}
                        value={current.size}
                        onChange={(v) => setItemField(area.key, it.key, "size", v)}
                      />
                    </div>
                  )}
                  {Number(current.quantity) > 0 && it.requiresDescription && (
                    <Form.Group className="mt-2">
                      <Form.Label className="small text-muted">Bitte beschreiben Sie den Gegenstand.</Form.Label>
                      <Form.Control
                        value={current.description || ""}
                        onChange={(e) => setItemField(area.key, it.key, "description", e.target.value)}
                      />
                    </Form.Group>
                  )}
                </div>
              );
            })
          )}
        </Accordion.Body>
      </Accordion.Item>
    ))}
  </Accordion>
);

const FurnitureSummary = ({ areas, items, heavyItems, assemblyAnswer, assemblyItems }) => {
  const { t } = useTranslation();
  const byArea = {};
  items.forEach((i) => {
    byArea[i.area] = byArea[i.area] || [];
    byArea[i.area].push(i);
  });

  return (
    <div>
      <StepHeader title="Ihre Auswahl" />
      <div className="mb-3">
        {areas.map((a) => (
          <Badge bg="light" text="dark" className="border me-2 mb-1" key={a}>
            {a}
          </Badge>
        ))}
      </div>

      {Object.entries(byArea).map(([area, list]) => (
        <div key={area} className="mb-2">
          <strong>{area}:</strong>
          <ul className="mb-1">
            {list.map((i, idx) => (
              <li key={idx}>
                {i.quantity} × {i.item}
                {i.type ? `, ${i.type}` : ""}
                {i.size ? `, ${i.size}` : ""}
                {i.description ? ` — ${i.description}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {heavyItems.length > 0 && (
        <>
          <h6>Schwere oder empfindliche Gegenstände</h6>
          <ul>
            {heavyItems.map((i, idx) => (
              <li key={idx}>
                {i.quantity} × {i.item}
                {i.size ? `, ${i.size}` : ""}
                {i.type ? `, ${i.type}` : ""}
              </li>
            ))}
          </ul>
        </>
      )}

      {assemblyAnswer === "Ja" && assemblyItems.length > 0 && (
        <>
          <h6>Ab- und Aufbauservice</h6>
          <ul>
            {assemblyItems.map((i, idx) => (
              <li key={idx}>
                {i.quantity} × {i.item} — {i.service}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

const FurnitureWizard = ({ onComplete, onEditRoute, initialData }) => {
  const { t } = useTranslation();
  const [areas, setAreas] = useState(() => initialData?.areas || []);
  const [items, setItems] = useState(() => dictByAreaItem(initialData?.items));
  const [heavyAnswer, setHeavyAnswer] = useState(
    () => initialData?.heavy_items_answer || (initialData?.heavy_items?.length ? "Ja" : "")
  );
  const [heavyItems, setHeavyItems] = useState(() => dictByKey(initialData?.heavy_items));
  const [assemblyAnswer, setAssemblyAnswer] = useState(() => initialData?.assembly_service?.answer || "");
  const [assemblyNotes, setAssemblyNotes] = useState(() => initialData?.assembly_service?.notes || "");
  const [assemblyConsult, setAssemblyConsult] = useState(() => !!initialData?.assembly_service?.consultation_requested);
  const [assemblyItems, setAssemblyItems] = useState(() => dictByKey(initialData?.assembly_service?.items));
  const [kitchenService, setKitchenService] = useState(() => initialData?.kitchen_service?.service || "");
  const [kitchenSize, setKitchenSize] = useState(() => initialData?.kitchen_service?.size || "");
  const [kitchenDescription, setKitchenDescription] = useState(() => initialData?.kitchen_service?.description || "");
  const [applianceAnswer, setApplianceAnswer] = useState(() => initialData?.appliance_connection_service?.answer || "");
  const [applianceItems, setApplianceItems] = useState(() => initialData?.appliance_connection_service?.items || []);
  const [helpCarry, setHelpCarry] = useState(() => initialData?.help_carry || "");
  const [pickupFloor, setPickupFloor] = useState(() => initialData?.pickup_floor || "");
  const [pickupElevator, setPickupElevator] = useState(() => !!initialData?.pickup_elevator);
  const [deliveryFloor, setDeliveryFloor] = useState(() => initialData?.delivery_floor || "");
  const [deliveryElevator, setDeliveryElevator] = useState(() => !!initialData?.delivery_elevator);
  const [images, setImages] = useState([]);
  const [notes, setNotes] = useState(() => initialData?.notes || "");
  const [error, setError] = useState("");

  const selectedAreaDefs = useMemo(() => FURNITURE_AREAS.filter((a) => areas.includes(a.key)), [areas]);

  const setItemField = (area, itemKey, field, value) => {
    const k = `${area}::${itemKey}`;
    setItems((prev) => ({ ...prev, [k]: { ...(prev[k] || { quantity: 0 }), area, item: itemKey, [field]: value } }));
  };

  const selectedItemsFlat = useMemo(() => Object.values(items).filter((i) => Number(i.quantity) > 0), [items]);

  const areaItemCount = (area) =>
    area.items.filter((it) => Number(items[`${area.key}::${it.key}`]?.quantity) > 0).length +
    (area.isCustomArea ? Object.values(items).filter((i) => i.area === area.key && Number(i.quantity) > 0).length : 0);

  const assemblableSelected = useMemo(() => {
    const eligible = new Set();
    selectedAreaDefs.forEach((area) => area.items.forEach((it) => it.assemblable && eligible.add(it.key)));
    return selectedItemsFlat.filter((i) => eligible.has(i.item));
  }, [selectedItemsFlat, selectedAreaDefs]);

  const kitchenSelected = selectedItemsFlat.some((i) => i.item === "Küchenzeile");
  const applianceCandidates = selectedItemsFlat.filter((i) => APPLIANCE_CONNECTION_ITEMS.includes(i.item)).map((i) => i.item);

  // The "Benötigen Sie einen Ab- und Aufbauservice?" question is only asked
  // when the customer actually picked something that can be taken apart
  // (Regal, Kleiderschrank, Bett, Küchenzeile, ...). If they only picked a
  // sofa, a TV or moving boxes there is nothing to disassemble, so asking
  // would just be noise — the question, and everything under it, is skipped
  // entirely and `assembly_service.answer` is saved as "Nein".
  const assemblyApplicable = assemblableSelected.length > 0 || kitchenSelected;

  const buildPayload = () => ({
    move_type: "Moebel_Einzelne_Gegenstaende",
    areas,
    items: selectedItemsFlat,
    heavy_items_answer: heavyAnswer,
    heavy_items: heavyAnswer === "Ja" ? Object.values(heavyItems).filter((i) => Number(i.quantity) > 0) : [],
    assembly_service: {
      // When no selected item is assemblable the question is never shown, so
      // we record an explicit "Nein" rather than leaving it empty.
      answer: assemblyApplicable ? assemblyAnswer : "Nein",
      notes: assemblyApplicable && assemblyAnswer === "Ich bin nicht sicher" ? assemblyNotes : null,
      consultation_requested: assemblyApplicable && assemblyAnswer === "Ich bin nicht sicher" ? assemblyConsult : null,
      items: assemblyApplicable && assemblyAnswer === "Ja" ? Object.values(assemblyItems).filter((i) => i.service) : [],
    },
    kitchen_service:
      assemblyApplicable && assemblyAnswer === "Ja" && kitchenSelected
        ? { service: kitchenService, size: kitchenSize, description: kitchenDescription }
        : null,
    appliance_connection_service: applianceCandidates.length > 0 ? { answer: applianceAnswer, items: applianceItems } : null,
    help_carry: helpCarry || "0",
    pickup_floor: helpCarry && helpCarry !== "0" ? pickupFloor || null : null,
    pickup_elevator: helpCarry && helpCarry !== "0" ? pickupElevator : false,
    delivery_floor: helpCarry && helpCarry !== "0" ? deliveryFloor || null : null,
    delivery_elevator: helpCarry && helpCarry !== "0" ? deliveryElevator : false,
    notes: notes || null,
  });

  const validate = () => {
    if (areas.length === 0) return "Bitte wählen Sie mindestens einen Bereich aus.";
    if (selectedItemsFlat.length === 0) return "Bitte wählen Sie mindestens einen Gegenstand aus.";
    if (selectedItemsFlat.find((i) => i.item === "Sonstiges" && !i.description))
      return "Bitte beschreiben Sie den Gegenstand (Sonstiges).";
    if (!heavyAnswer) return "Bitte beantworten Sie die Frage zu schweren/empfindlichen Gegenständen.";
    if (assemblyApplicable) {
      if (!assemblyAnswer) return "Bitte beantworten Sie die Frage zum Ab- und Aufbauservice.";
      if (assemblyAnswer === "Ja") {
        const invalid = Object.values(assemblyItems).some((i) => {
          const original = selectedItemsFlat.find((s) => s.item === i.item);
          return i.service && original && Number(i.quantity) > Number(original.quantity);
        });
        if (invalid) return "Die Anzahl beim Ab-/Aufbau darf die ursprünglich gewählte Anzahl nicht übersteigen.";
      }
    }
    if (!helpCarry) return "Bitte beantworten Sie die Frage zur Be-/Entladehilfe.";
    return null;
  };

  const canSubmit =
    areas.length > 0 &&
    selectedItemsFlat.length > 0 &&
    !!heavyAnswer &&
    (!assemblyApplicable || !!assemblyAnswer) &&
    !!helpCarry;

  const handleSubmit = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    onComplete(buildPayload(), images);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Card className="p-3 mb-4">
      {error && (
        <Alert variant="danger" className="d-flex align-items-center gap-2">
          <FaExclamationTriangle /> {error}
        </Alert>
      )}

      <Section show first>
        <StepHeader title="Wo befinden sich die Möbel oder Gegenstände?" subtitle="Sie können mehrere Bereiche auswählen." />
        <MultiSelect name="area" options={FURNITURE_AREAS.map((a) => a.key)} values={areas} onChange={setAreas} />
      </Section>

      <Section show={areas.length > 0}>
        <SectionDivider />
        <StepHeader title="Wählen Sie die Möbel oder Gegenstände aus" />
        <ItemsAccordion areaDefs={selectedAreaDefs} items={items} setItemField={setItemField} areaItemCount={areaItemCount} />
        {selectedItemsFlat.length > 0 && <div className="text-muted small mt-2">{selectedItemsFlat.length} Gegenstände ausgewählt</div>}
      </Section>

      <Section show={selectedItemsFlat.length > 0}>
        <SectionDivider />
        <StepHeader title="Möchten Sie schwere oder empfindliche Gegenstände hinzufügen?" />
        <SingleSelect name="heavy-answer" options={[t("Ja"), t("Nein")]} value={heavyAnswer} onChange={setHeavyAnswer} />
        {heavyAnswer === "Ja" && (
          <div className="mt-3">
            {HEAVY_ITEMS.map((it) => {
              const current = heavyItems[it.key] || { quantity: 0 };
              return (
                <div key={it.key} className="mb-2 pb-2 border-bottom">
                  <Row className="align-items-center">
                    <Col xs={7} sm={5}>
                      {it.key}
                    </Col>
                    <Col xs={5} sm={3}>
                      <Counter
                        value={current.quantity}
                        onChange={(v) => setHeavyItems((prev) => ({ ...prev, [it.key]: { ...current, item: it.key, quantity: v } }))}
                      />
                    </Col>
                  </Row>
                  {Number(current.quantity) > 0 && it.warning && (
                    <Alert variant="warning" className="mt-2 py-2 small">
                      {it.warning}
                    </Alert>
                  )}
                  {Number(current.quantity) > 0 && it.typeOptions && (
                    <SingleSelect
                      name={`${it.key}-type`}
                      options={it.typeOptions}
                      value={current.type}
                      onChange={(v) => setHeavyItems((prev) => ({ ...prev, [it.key]: { ...current, type: v } }))}
                    />
                  )}
                  {Number(current.quantity) > 0 && it.sizeOptions && (
                    <SingleSelect
                      name={`${it.key}-size`}
                      options={it.sizeOptions}
                      value={current.size}
                      onChange={(v) => setHeavyItems((prev) => ({ ...prev, [it.key]: { ...current, size: v } }))}
                    />
                  )}
                  {Number(current.quantity) > 0 && it.requiresDescription && (
                    <Form.Control
                      className="mt-2"
                      placeholder="Bitte beschreiben Sie den Gegenstand."
                      value={current.description || ""}
                      onChange={(e) => setHeavyItems((prev) => ({ ...prev, [it.key]: { ...current, description: e.target.value } }))}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Skipped entirely when nothing assemblable was selected (e.g. only a
          sofa, a TV or moving boxes) — see `assemblyApplicable`. */}
      <Section show={!!heavyAnswer && assemblyApplicable}>
        <SectionDivider />
        <StepHeader title="Benötigen Sie einen Ab- und Aufbauservice?" />
        <SingleSelect name="assembly-answer" options={ASSEMBLY_SERVICE_OPTIONS} value={assemblyAnswer} onChange={setAssemblyAnswer} />

        {assemblyAnswer === "Ich bin nicht sicher" && (
          <>
            <Form.Group className="mt-2">
              <Form.Label className="small text-muted">Bitte beschreiben Sie kurz, wobei Sie Hilfe benötigen.</Form.Label>
              <Form.Control as="textarea" rows={2} value={assemblyNotes} onChange={(e) => setAssemblyNotes(e.target.value)} />
            </Form.Group>
            <Form.Check
              className="mt-2"
              type="checkbox"
              id="assembly-consult"
              label="Ich möchte beraten werden"
              checked={assemblyConsult}
              onChange={(e) => setAssemblyConsult(e.target.checked)}
            />
          </>
        )}

        {assemblyAnswer === "Ja" && (
          <div className="mt-3">
            {assemblableSelected.map((sel) => {
              const current = assemblyItems[sel.item] || { quantity: sel.quantity };
              return (
                <div key={sel.item} className="mb-3 pb-2 border-bottom">
                  <Form.Check
                    type="checkbox"
                    id={`assembly-${sel.item}`}
                    label={`${sel.item}${sel.type ? ` – ${sel.type}` : ""}${sel.size ? ` – ${sel.size}` : ""}`}
                    checked={!!assemblyItems[sel.item]}
                    onChange={(e) =>
                      setAssemblyItems((prev) => {
                        const next = { ...prev };
                        if (e.target.checked) next[sel.item] = { item: sel.item, quantity: sel.quantity, service: "" };
                        else delete next[sel.item];
                        return next;
                      })
                    }
                  />
                  {assemblyItems[sel.item] && (
                    <div className="ms-4 mt-2">
                      <div className="small text-muted mb-1">Welche Leistung benötigen Sie?</div>
                      <SingleSelect
                        name={`${sel.item}-service`}
                        options={ASSEMBLY_LEVEL_OPTIONS}
                        value={current.service}
                        onChange={(v) => setAssemblyItems((prev) => ({ ...prev, [sel.item]: { ...prev[sel.item], service: v } }))}
                      />
                      <div className="small text-muted mb-1">Anzahl für den Ab- und Aufbauservice (max. {sel.quantity})</div>
                      <Counter
                        value={current.quantity}
                        max={sel.quantity}
                        onChange={(v) => setAssemblyItems((prev) => ({ ...prev, [sel.item]: { ...prev[sel.item], quantity: v } }))}
                      />
                      <Form.Control
                        className="mt-2"
                        size="sm"
                        placeholder="Zusätzliche Hinweise (optional)"
                        value={current.notes || ""}
                        onChange={(e) => setAssemblyItems((prev) => ({ ...prev, [sel.item]: { ...prev[sel.item], notes: e.target.value } }))}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            {kitchenSelected && (
              <div className="mb-3 pb-2 border-bottom">
                <h6>Welche Leistung benötigen Sie für die Küche?</h6>
                <SingleSelect name="kitchen-service" options={KITCHEN_SERVICE_OPTIONS} value={kitchenService} onChange={setKitchenService} />
                <div className="small text-muted mb-1">Wie groß ist die Küche ungefähr?</div>
                <SingleSelect name="kitchen-size" options={KITCHEN_SIZE_OPTIONS} value={kitchenSize} onChange={setKitchenSize} />
                <Form.Control
                  className="mt-2"
                  placeholder="Bitte beschreiben Sie kurz Ihre Küche."
                  value={kitchenDescription}
                  onChange={(e) => setKitchenDescription(e.target.value)}
                />
              </div>
            )}

          </div>
        )}
      </Section>

      {/* Connecting/disconnecting washing machines, dishwashers etc. is a
          separate service from furniture assembly (a washing machine is never
          "assembled"), so this question stands on its own and is shown
          whenever such an appliance was selected — regardless of the answer
          to the assembly question above. */}
      <Section show={!!heavyAnswer && applianceCandidates.length > 0}>
        <SectionDivider />
        <StepHeader title="Müssen Geräte angeschlossen oder abgeklemmt werden?" />
        <SingleSelect name="appliance-answer" options={APPLIANCE_CONNECTION_OPTIONS} value={applianceAnswer} onChange={setApplianceAnswer} />
        {applianceAnswer && applianceAnswer !== "Nein" && (
          <MultiSelect name="appliance-items" options={applianceCandidates} values={applianceItems} onChange={setApplianceItems} />
        )}
        <div className="small text-muted mt-1">
          Elektro-, Gas- und Wasseranschlüsse werden nur ausgeführt, wenn dies fachlich und rechtlich möglich ist.
        </div>
      </Section>

      <Section show={!!heavyAnswer && (!assemblyApplicable || !!assemblyAnswer)}>
        <SectionDivider />
        <HelpAndFloorsStep
          helpCarry={helpCarry}
          setHelpCarry={setHelpCarry}
          pickupFloor={pickupFloor}
          setPickupFloor={setPickupFloor}
          pickupElevator={pickupElevator}
          setPickupElevator={setPickupElevator}
          deliveryFloor={deliveryFloor}
          setDeliveryFloor={setDeliveryFloor}
          deliveryElevator={deliveryElevator}
          setDeliveryElevator={setDeliveryElevator}
        />
      </Section>

      <Section show={!!helpCarry}>
        <SectionDivider />
        <StepHeader title="Fotos & Hinweise" subtitle="Optional" />
        {/* <Form.Group className="mb-3">
          <Form.Label>Fotos hochladen</Form.Label>
          <Form.Control type="file" multiple accept="image/*,.pdf" onChange={(e) => setImages(Array.from(e.target.files || []))} />
        </Form.Group>
        <Form.Group>
          <Form.Label>Zusätzliche Hinweise</Form.Label>
          <Form.Control as="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Form.Group> */}

        <Card className="p-3 p-md-4 mb-4 border-0 rounded-3 bg-light">
          <h5 className="fw-bold mb-3">{t("dynamic_form.photos")}</h5>
          <Form.Group className="mb-3">
            <Form.Label>{t("dynamic_form.add_photos")}</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setImages(Array.from(e.target.files || []))}
            />
            <Form.Text className="text-muted">
              {t("dynamic_form.photos_hint")}
            </Form.Text>
          </Form.Group>

          {images.length > 0 && (
            <div className="mt-3">
              <h6>{t("dynamic_form.selected_photos")}</h6>
              <Row>
                {images.map((image, index) => (
                  <Col
                    xs={6}
                    sm={4}
                    md={3}
                    key={index}
                    className="mb-2 position-relative"
                  >
                    {image instanceof File ? (
                      <img
                        src={URL.createObjectURL(image)}
                        alt={t("dynamic_form.upload_alt", { index: index + 1 })}
                        style={{
                          width: "100%",
                          height: "80px",
                          objectFit: "cover",
                        }}
                        className="rounded"
                      />
                    ) : (
                      <img
                        src={image}
                        alt={t("dynamic_form.upload_alt", { index: index + 1 })}
                        style={{
                          width: "100%",
                          height: "80px",
                          objectFit: "cover",
                        }}
                        className="rounded"
                        onError={(e) => {
                          console.error("Error loading image:", image);
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0"
                      onClick={() => removeImage(index)}
                      style={{ transform: "translate(50%, -50%)" }}
                    >
                      ×
                    </Button>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Card>

        <Card className="p-3 p-md-4 mb-4 border-0 rounded-3 bg-light">
          <h5 className="fw-bold mb-3">{t("dynamic_form.additional_notes")}</h5>
          <Form.Group>
            <Form.Label>{t("dynamic_form.description")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder={t("dynamic_form.description_placeholder")}
              value={notes} 
              onChange={(e) => setNotes(e.target.value)}
            />
          </Form.Group>
        </Card>

        <SectionDivider />
        <FurnitureSummary
          areas={areas}
          items={selectedItemsFlat}
          heavyItems={heavyAnswer === "Ja" ? Object.values(heavyItems).filter((i) => i.quantity > 0) : []}
          assemblyAnswer={assemblyAnswer}
          assemblyItems={Object.values(assemblyItems).filter((i) => i.service)}
        />
      </Section>

      <StickyActionBar
        onBack={onEditRoute}
        onSubmit={handleSubmit}
        disabled={!canSubmit}
        disabledHint={!canSubmit ? "Bitte beantworten Sie zuerst alle Fragen oben." : null}
      />
    </Card>
  );
};

/* ----------------------------------------------------------------------- */
/* BUILDING MATERIALS (Baumaterial) — one continuous page                  */
/* ----------------------------------------------------------------------- */

const MaterialQuantityForm = ({ mat, value = {}, onChange }) => {
  const { t } = useTranslation();
  const set = (field, v) => onChange({ ...value, item: mat.key, [field]: v });

  switch (mat.unitType) {
    case "piece":
      return (
        <>
          <Counter value={value.quantity} onChange={(v) => set("quantity", v)} />
          {mat.glazable && (
            <>
              <div className="small text-muted mt-2 mb-1">Sind die Teile verglast?</div>
              <SingleSelect name={`${mat.key}-glazed`} options={GLAZED_OPTIONS} value={value.glazed} onChange={(v) => set("glazed", v)} />
              <div className="small text-muted mb-1">Größe des größten Teils</div>
              <SingleSelect name={`${mat.key}-size`} options={DOOR_WINDOW_SIZE_OPTIONS} value={value.max_size} onChange={(v) => set("max_size", v)} />
            </>
          )}
          {mat.packagedCheck && (
            <>
              <div className="small text-muted mt-2 mb-1">Ist der Gegenstand neu verpackt?</div>
              <SingleSelect name={`${mat.key}-packaged`} options={PACKAGED_OPTIONS} value={value.packaged} onChange={(v) => set("packaged", v)} />
            </>
          )}
        </>
      );
    case "package":
      return (
        <>
          <div className="small text-muted mb-1">Anzahl der Säcke / Verpackungen</div>
          <Counter value={value.quantity} onChange={(v) => set("quantity", v)} />
          <div className="small text-muted mt-2 mb-1">Gewicht pro Einheit</div>
          <SingleSelect name={`${mat.key}-w`} options={PACKAGE_WEIGHT_BUCKETS} value={value.weight_per_unit_kg} onChange={(v) => set("weight_per_unit_kg", v)} />
        </>
      );
    case "area":
      return (
        <>
          <Form.Group className="mb-2">
            <Form.Label className="small text-muted">Ungefähre Menge (m²)</Form.Label>
            <Form.Control type="number" min={0} value={value.quantity || ""} onChange={(e) => set("quantity", e.target.value)} />
          </Form.Group>
          <div className="small text-muted mb-1">Anzahl der Pakete oder Paletten</div>
          <Counter value={value.pallets} onChange={(v) => set("pallets", v)} />
        </>
      );
    case "length":
      return (
        <>
          <Form.Group className="mb-2">
            <Form.Label className="small text-muted">{mat.subtype === "sheet" ? "Anzahl der Platten" : "Anzahl der Teile"}</Form.Label>
            <Counter value={value.quantity} onChange={(v) => set("quantity", v)} />
          </Form.Group>
          {mat.subtype === "sheet" ? (
            <>
              <div className="small text-muted mb-1">Größe der größten Platte</div>
              <SingleSelect name={`${mat.key}-len`} options={SHEET_LENGTH_BUCKETS} value={value.max_length} onChange={(v) => set("max_length", v)} />
              <div className="small text-muted mt-2 mb-1">Müssen die Platten liegend transportiert werden?</div>
              <SingleSelect name={`${mat.key}-lie`} options={[t("Ja"), t("Nein"), t("Nicht bekannt")]} value={value.lying_required} onChange={(v) => set("lying_required", v)} />
            </>
          ) : (
            <>
              <div className="small text-muted mb-1">Länge des längsten Teils</div>
              <SingleSelect name={`${mat.key}-len`} options={LENGTH_BUCKETS} value={value.max_length} onChange={(v) => set("max_length", v)} />
              <div className="small text-muted mt-2 mb-1">Gewicht des schwersten Teils</div>
              <SingleSelect name={`${mat.key}-w`} options={LENGTH_WEIGHT_BUCKETS} value={value.weight_per_unit_kg} onChange={(v) => set("weight_per_unit_kg", v)} />
            </>
          )}
        </>
      );
    case "bulk":
      return (
        <>
          <div className="small text-muted mb-1">Wie ist das Material verpackt?</div>
          <SingleSelect name={`${mat.key}-pack`} options={BULK_PACKAGING_OPTIONS} value={value.packaging} onChange={(v) => set("packaging", v)} />
          <div className="small text-muted mt-2 mb-1">Ungefähre Menge</div>
          <SingleSelect name={`${mat.key}-qty`} options={BULK_WEIGHT_BUCKETS} value={value.total_weight_kg} onChange={(v) => set("total_weight_kg", v)} />
        </>
      );
    case "pallet":
      if (mat.subtype === "tiles") {
        return (
          <>
            <div className="small text-muted mb-1">Wie sind die Fliesen verpackt?</div>
            <SingleSelect name={`${mat.key}-pack`} options={TILE_PACKAGING_OPTIONS} value={value.packaging} onChange={(v) => set("packaging", v)} />
            <div className="small text-muted mt-2 mb-1">Anzahl der Kartons / Paletten</div>
            <Counter value={value.quantity} onChange={(v) => set("quantity", v)} />
            <div className="small text-muted mt-2 mb-1">Ungefähres Gesamtgewicht</div>
            <SingleSelect name={`${mat.key}-w`} options={TILE_WEIGHT_BUCKETS} value={value.total_weight_kg} onChange={(v) => set("total_weight_kg", v)} />
          </>
        );
      }
      if (mat.subtype === "slabs") {
        return (
          <>
            <div className="small text-muted mb-1">Sind die Platten einzeln oder auf einem Gestell verpackt?</div>
            <SingleSelect name={`${mat.key}-pack`} options={SLAB_PACKAGING_OPTIONS} value={value.packaging} onChange={(v) => set("packaging", v)} />
            <div className="small text-muted mt-2 mb-1">Anzahl der Platten</div>
            <Counter value={value.quantity} onChange={(v) => set("quantity", v)} />
            <div className="small text-muted mt-2 mb-1">Größe der größten Platte</div>
            <SingleSelect name={`${mat.key}-size`} options={SLAB_SIZE_BUCKETS} value={value.max_size} onChange={(v) => set("max_size", v)} />
            <div className="small text-muted mt-2 mb-1">Gewicht (pro Platte)</div>
            <SingleSelect name={`${mat.key}-w`} options={SLAB_WEIGHT_BUCKETS} value={value.weight_per_unit_kg} onChange={(v) => set("weight_per_unit_kg", v)} />
          </>
        );
      }
      return (
        <>
          <div className="small text-muted mb-1">Wie ist das Material verpackt?</div>
          <SingleSelect name={`${mat.key}-pack`} options={BRICK_PACKAGING_OPTIONS} value={value.packaging} onChange={(v) => set("packaging", v)} />
          <div className="small text-muted mt-2 mb-1">Anzahl der Paletten / Kisten</div>
          <Counter value={value.quantity} onChange={(v) => set("quantity", v)} />
          <div className="small text-muted mt-2 mb-1">Ungefähres Gesamtgewicht</div>
          <SingleSelect name={`${mat.key}-w`} options={BRICK_WEIGHT_BUCKETS} value={value.total_weight_kg} onChange={(v) => set("total_weight_kg", v)} />
        </>
      );
    case "machine":
      return (
        <>
          <div className="small text-muted mb-1">Welche Geräte oder Maschinen?</div>
          <MultiSelect name={`${mat.key}-types`} options={MACHINE_TYPES} values={value.machine_types || []} onChange={(v) => set("machine_types", v)} />
          <div className="small text-muted mt-2 mb-1">Anzahl</div>
          <Counter value={value.quantity} onChange={(v) => set("quantity", v)} />
          <div className="small text-muted mt-2 mb-1">Gewicht</div>
          <SingleSelect name={`${mat.key}-w`} options={MACHINE_WEIGHT_BUCKETS} value={value.weight_per_unit_kg} onChange={(v) => set("weight_per_unit_kg", v)} />
          {["101–250 kg", "Über 250 kg"].includes(value.weight_per_unit_kg) && (
            <>
              <div className="small text-muted mt-2 mb-1">Ist eine Hebehilfe vorhanden?</div>
              <SingleSelect name={`${mat.key}-lift`} options={[t("Ja"), t("Nein"), t("Nicht bekannt")]} value={value.lift_aid} onChange={(v) => set("lift_aid", v)} />
            </>
          )}
        </>
      );
    case "custom":
      return (
        <>
          <Form.Group className="mb-2">
            <Form.Label className="small text-muted">Bitte beschreiben Sie das Baumaterial.</Form.Label>
            <Form.Control value={value.description || ""} onChange={(e) => set("description", e.target.value)} />
          </Form.Group>
          <div className="small text-muted mb-1">Anzahl oder Menge</div>
          <Counter value={value.quantity} onChange={(v) => set("quantity", v)} />
          <div className="small text-muted mt-2 mb-1">Ungefähres Gewicht</div>
          <SingleSelect name={`${mat.key}-w`} options={CUSTOM_MATERIAL_WEIGHT_BUCKETS} value={value.total_weight_kg} onChange={(v) => set("total_weight_kg", v)} />
        </>
      );
    default:
      return null;
  }
};

const LocationBlock = ({ title, distanceLabel, value = {}, onChange }) => {
  const { t } = useTranslation();
  const set = (field, v) => onChange({ ...value, [field]: v });
  const needsFloor = value.location_type === "Obergeschoss" || value.location_type === "Keller";

  return (
    <div className="mb-3">
      <h6>{title}</h6>
      <SingleSelect name={`${title}-loc`} options={LOCATION_TYPES} value={value.location_type} onChange={(v) => set("location_type", v)} />
      {needsFloor && (
        <Form.Group className="mb-2">
          <Form.Label className="small text-muted">In welchem Stockwerk?</Form.Label>
          <Form.Control value={value.floor || ""} onChange={(e) => set("floor", e.target.value)} />
          <div className="small text-muted mt-1 mb-1">Ist ein Lastenaufzug vorhanden?</div>
          <SingleSelect name={`${title}-lift`} options={[t("Ja"), t("Nein"), t("Nicht bekannt")]} value={value.freight_elevator} onChange={(v) => set("freight_elevator", v)} />
        </Form.Group>
      )}
      <div className="small text-muted mb-1">{distanceLabel}</div>
      <SingleSelect name={`${title}-dist`} options={CARRYING_DISTANCE_BUCKETS} value={value.carrying_distance} onChange={(v) => set("carrying_distance", v)} />
      <div className="small text-muted mt-2 mb-1">Kann das Transportfahrzeug direkt am Ort halten?</div>
      <SingleSelect name={`${title}-access`} options={[t("Ja"), t("Nein"), t("Nicht bekannt")]} value={value.direct_access} onChange={(v) => set("direct_access", v)} />
      {value.direct_access === "Nein" && (
        <SingleSelect name={`${title}-access-dist`} options={VEHICLE_ACCESS_DISTANCE_BUCKETS} value={value.access_distance} onChange={(v) => set("access_distance", v)} />
      )}
    </div>
  );
};

const BuildingMaterialsWizard = ({ onComplete, onEditRoute, initialData }) => {
  const { t } = useTranslation();
  const [selectedKeys, setSelectedKeys] = useState(() => initialData?.materials?.map((m) => m.item) || []);
  const [materialData, setMaterialData] = useState(() => dictByKey(initialData?.materials));
  const [totalWeightEstimate, setTotalWeightEstimate] = useState(() => initialData?.total_weight_estimate || "");
  const [packaging, setPackaging] = useState(() => initialData?.packaging || []);
  const [palletCount, setPalletCount] = useState(() => initialData?.pallets?.count || 0);
  const [palletType, setPalletType] = useState(() => initialData?.pallets?.type || "");
  const [palletWeight, setPalletWeight] = useState(() => initialData?.pallets?.weight_per_pallet || "");
  const [pickup, setPickup] = useState(() => initialData?.pickup || {});
  const [delivery, setDelivery] = useState(() => initialData?.delivery || {});
  const [loadingServiceAnswer, setLoadingServiceAnswer] = useState(() => initialData?.loading_service?.service || "");
  const [helpers, setHelpers] = useState(() => initialData?.loading_service?.helpers || "");
  const [equipment, setEquipment] = useState(() => initialData?.loading_service?.equipment || []);
  const [equipmentProvider, setEquipmentProvider] = useState(() => initialData?.loading_service?.equipment_provider || "");
  const [demontageAnswer, setDemontageAnswer] = useState(() => initialData?.demontage_service?.answer || "");
  const [demontageNotes, setDemontageNotes] = useState(() => initialData?.demontage_service?.notes || "");
  const [demontageConsult, setDemontageConsult] = useState(() => !!initialData?.demontage_service?.consultation_requested);
  const [demontageItems, setDemontageItems] = useState(() => dictByKey(initialData?.demontage_service?.items));
  const [images, setImages] = useState([]);
  const [notes, setNotes] = useState(() => initialData?.notes || "");
  const [error, setError] = useState("");

  const selectedMaterials = BUILDING_MATERIALS.filter((m) => selectedKeys.includes(m.key));
  const needsCrane = equipment.some((e) => EQUIPMENT_NEEDS_PROVIDER.includes(e));
  const demontageEligible = selectedMaterials.filter((m) => ["Türen", "Fenster", "Heizkörper", "Sanitäranlagen", "Duschkabine"].includes(m.key));

  const totalWeightAuto = Object.values(materialData).reduce((sum, m) => sum + (bucketToKg(m.total_weight_kg) || 0), 0);

  const buildPayload = () => ({
    move_type: "Baumaterial",
    materials: selectedMaterials.map((m) => ({ unit: m.unitType, ...materialData[m.key] })),
    total_weight_estimate: totalWeightEstimate,
    packaging,
    pallets: packaging.includes("Auf Paletten") ? { count: palletCount, type: palletType, weight_per_pallet: palletWeight } : null,
    pickup,
    delivery,
    loading_service: {
      service: loadingServiceAnswer,
      helpers: loadingServiceAnswer && loadingServiceAnswer !== "Nur Transport" ? helpers : null,
      equipment,
      equipment_provider: needsCrane ? equipmentProvider : null,
    },
    demontage_service: {
      answer: demontageAnswer,
      notes: demontageAnswer === "Ich bin nicht sicher" ? demontageNotes : null,
      consultation_requested: demontageAnswer === "Ich bin nicht sicher" ? demontageConsult : null,
      items: demontageAnswer === "Ja" ? Object.values(demontageItems).filter((i) => i.service) : [],
    },
    notes: notes || null,
    manual_review_required:
      totalWeightEstimate === "Über 3,5 Tonnen" ||
      packaging.includes("Lose") ||
      Object.values(materialData).some((m) => m.max_length === "Über 4 Meter"),
  });

  const validate = () => {
    if (selectedKeys.length === 0) return "Bitte wählen Sie mindestens ein Baumaterial aus.";
    const missing = selectedMaterials.find((m) => m.requiresDescription && !materialData[m.key]?.description);
    if (missing) return `Bitte beschreiben Sie: ${missing.key}`;
    if (!totalWeightEstimate) return "Bitte geben Sie das ungefähre Gesamtgewicht an.";
    if (packaging.length === 0) return "Bitte geben Sie an, wie die Materialien verpackt sind.";
    if (!pickup.location_type) return "Bitte geben Sie den Abholort an.";
    if (!delivery.location_type) return "Bitte geben Sie den Zielort an.";
    if (!loadingServiceAnswer) return "Bitte beantworten Sie die Frage zur Be-/Entladehilfe.";
    if (!demontageAnswer) return "Bitte beantworten Sie die Frage zu Demontage/Montage.";
    return null;
  };

  const canSubmit =
    selectedKeys.length > 0 &&
    !!totalWeightEstimate &&
    packaging.length > 0 &&
    !!pickup.location_type &&
    !!delivery.location_type &&
    !!loadingServiceAnswer &&
    !!demontageAnswer;

  const handleSubmit = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    onComplete(buildPayload(), images);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Card className="p-3 mb-4">
      {error && (
        <Alert variant="danger" className="d-flex align-items-center gap-2">
          <FaExclamationTriangle /> {error}
        </Alert>
      )}

      <Section show first>
        <StepHeader title="Welche Baumaterialien möchten Sie transportieren?" />
        <MultiSelect name="materials" options={BUILDING_MATERIALS.map((m) => m.key)} values={selectedKeys} onChange={setSelectedKeys} />
      </Section>

      <Section show={selectedKeys.length > 0}>
        <SectionDivider />
        <StepHeader title="Menge und Details je Material" />
        <Accordion defaultActiveKey={selectedMaterials[0]?.key} alwaysOpen>
          {selectedMaterials.map((m) => (
            <Accordion.Item eventKey={m.key} key={m.key}>
              <Accordion.Header>{m.key}</Accordion.Header>
              <Accordion.Body>
                <MaterialQuantityForm mat={m} value={materialData[m.key]} onChange={(v) => setMaterialData((prev) => ({ ...prev, [m.key]: v }))} />
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </Section>

      <Section show={selectedKeys.length > 0}>
        <SectionDivider />
        <StepHeader title="Wie hoch ist das ungefähre Gesamtgewicht?" />
        <SingleSelect name="total-weight" options={TOTAL_WEIGHT_BUCKETS} value={totalWeightEstimate} onChange={setTotalWeightEstimate} />
        {totalWeightEstimate === "Über 3,5 Tonnen" && (
          <Alert variant="warning" className="mt-2 small">
            Dieser Auftrag wird nicht automatisch bepreist und geht an unser Team zur manuellen Prüfung.
          </Alert>
        )}
      </Section>

      <Section show={!!totalWeightEstimate}>
        <SectionDivider />
        <StepHeader title="Wie sind die Baumaterialien verpackt?" />
        <MultiSelect name="packaging" options={PACKAGING_METHODS} values={packaging} onChange={setPackaging} />
        {packaging.includes("Auf Paletten") && (
          <div className="mt-3">
            <div className="small text-muted mb-1">Anzahl der Paletten</div>
            <Counter value={palletCount} onChange={setPalletCount} />
            <div className="small text-muted mt-2 mb-1">Palettentyp</div>
            <SingleSelect name="pallet-type" options={PALLET_TYPES} value={palletType} onChange={setPalletType} />
            <div className="small text-muted mt-2 mb-1">Gewicht pro Palette</div>
            <SingleSelect name="pallet-weight" options={PALLET_WEIGHT_BUCKETS} value={palletWeight} onChange={setPalletWeight} />
          </div>
        )}
      </Section>

      <Section show={packaging.length > 0}>
        <SectionDivider />
        <LocationBlock title="Wo müssen die Baumaterialien abgeholt werden?" distanceLabel="Wie weit ist der Transportweg vom Material zum Fahrzeug?" value={pickup} onChange={setPickup} />
      </Section>

      <Section show={!!pickup.location_type}>
        <SectionDivider />
        <LocationBlock title="Wo sollen die Baumaterialien abgeladen werden?" distanceLabel="Wie weit ist der Transportweg vom Fahrzeug zum Abladeort?" value={delivery} onChange={setDelivery} />
      </Section>

      <Section show={!!delivery.location_type}>
        <SectionDivider />
        <StepHeader title="Benötigen Sie Hilfe beim Be- und Entladen?" />
        <SingleSelect name="loading-service" options={LOADING_SERVICE_OPTIONS} value={loadingServiceAnswer} onChange={setLoadingServiceAnswer} />
        {loadingServiceAnswer && loadingServiceAnswer !== "Nur Transport" && (
          <>
            <div className="small text-muted mt-2 mb-1">Wie viele Helfer werden benötigt?</div>
            <SingleSelect name="helpers" options={HELPERS_OPTIONS} value={helpers} onChange={setHelpers} />
          </>
        )}
        {loadingServiceAnswer && (
          <>
            <div className="small text-muted mt-3 mb-1">Werden besondere Hilfsmittel benötigt?</div>
            <MultiSelect name="equipment" options={SPECIAL_EQUIPMENT_OPTIONS} values={equipment} onChange={setEquipment} />
            {needsCrane && (
              <>
                <div className="small text-muted mt-2 mb-1">Wer stellt das Gerät bereit?</div>
                <SingleSelect name="equip-provider" options={EQUIPMENT_PROVIDER_OPTIONS} value={equipmentProvider} onChange={setEquipmentProvider} />
              </>
            )}
          </>
        )}
      </Section>

      <Section show={!!loadingServiceAnswer}>
        <SectionDivider />
        <StepHeader title="Müssen Bauteile demontiert oder montiert werden?" />
        <SingleSelect name="demontage-answer" options={DEMONTAGE_ANSWER_OPTIONS} value={demontageAnswer} onChange={setDemontageAnswer} />
        {demontageAnswer === "Ich bin nicht sicher" && (
          <>
            <Form.Control
              as="textarea"
              rows={2}
              className="mt-2"
              placeholder="Bitte beschreiben Sie kurz, wobei Sie Unterstützung benötigen."
              value={demontageNotes}
              onChange={(e) => setDemontageNotes(e.target.value)}
            />
            <Form.Check
              className="mt-2"
              type="checkbox"
              id="demontage-consult"
              label="Ich möchte beraten werden"
              checked={demontageConsult}
              onChange={(e) => setDemontageConsult(e.target.checked)}
            />
          </>
        )}
        {demontageAnswer === "Ja" && (
          <div className="mt-3">
            {demontageEligible.length === 0 && (
              <div className="text-muted small">Keines der ausgewählten Materialien ist typischerweise demontierbar/montierbar.</div>
            )}
            {demontageEligible.map((m) => {
              const current = demontageItems[m.key] || { quantity: materialData[m.key]?.quantity || 0 };
              return (
                <div key={m.key} className="mb-3 pb-2 border-bottom">
                  <Form.Check
                    type="checkbox"
                    id={`demontage-${m.key}`}
                    label={m.key}
                    checked={!!demontageItems[m.key]}
                    onChange={(e) =>
                      setDemontageItems((prev) => {
                        const next = { ...prev };
                        if (e.target.checked) next[m.key] = { item: m.key, quantity: materialData[m.key]?.quantity || 1, service: "" };
                        else delete next[m.key];
                        return next;
                      })
                    }
                  />
                  {demontageItems[m.key] && (
                    <div className="ms-4 mt-2">
                      <SingleSelect
                        name={`${m.key}-service`}
                        options={DEMONTAGE_SERVICE_OPTIONS}
                        value={current.service}
                        onChange={(v) => setDemontageItems((prev) => ({ ...prev, [m.key]: { ...prev[m.key], service: v } }))}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            <Alert variant="secondary" className="small mt-2">
              Elektro-, Gas-, Wasser- und bauliche Arbeiten werden nur ausgeführt, wenn dies fachlich, technisch und rechtlich möglich ist.
            </Alert>
          </div>
        )}
      </Section>

      <Section show={!!demontageAnswer}>
        <SectionDivider />
        <StepHeader title="Fotos & Hinweise" subtitle="Optional" />
        <Card className="p-3 p-md-4 mb-4 border-0 rounded-3 bg-light">
          <h5 className="fw-bold mb-3">{t("dynamic_form.photos")}</h5>
          <Form.Group className="mb-3">
            <Form.Label>{t("dynamic_form.add_photos")}</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setImages(Array.from(e.target.files || []))}
            />
            <Form.Text className="text-muted">
              {t("dynamic_form.photos_hint")}
            </Form.Text>
          </Form.Group>

          {images.length > 0 && (
            <div className="mt-3">
              <h6>{t("dynamic_form.selected_photos")}</h6>
              <Row>
                {images.map((image, index) => (
                  <Col
                    xs={6}
                    sm={4}
                    md={3}
                    key={index}
                    className="mb-2 position-relative"
                  >
                    {image instanceof File ? (
                      <img
                        src={URL.createObjectURL(image)}
                        alt={t("dynamic_form.upload_alt", { index: index + 1 })}
                        style={{
                          width: "100%",
                          height: "80px",
                          objectFit: "cover",
                        }}
                        className="rounded"
                      />
                    ) : (
                      <img
                        src={image}
                        alt={t("dynamic_form.upload_alt", { index: index + 1 })}
                        style={{
                          width: "100%",
                          height: "80px",
                          objectFit: "cover",
                        }}
                        className="rounded"
                        onError={(e) => {
                          console.error("Error loading image:", image);
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0"
                      onClick={() => removeImage(index)}
                      style={{ transform: "translate(50%, -50%)" }}
                    >
                      ×
                    </Button>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Card>

        <Card className="p-3 p-md-4 mb-4 border-0 rounded-3 bg-light">
          <h5 className="fw-bold mb-3">{t("dynamic_form.additional_notes")}</h5>
          <Form.Group>
            <Form.Label>{t("dynamic_form.description")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder={t("dynamic_form.description_placeholder")}
              value={notes} 
              onChange={(e) => setNotes(e.target.value)}
            />
          </Form.Group>
        </Card>

        <SectionDivider />
        <StepHeader title="Ihre Auswahl" />
        <ul>
          {selectedMaterials.map((m) => (
            <li key={m.key}>
              {m.key}
              {materialData[m.key]?.quantity ? ` — ${materialData[m.key].quantity} ${m.unitType}` : ""}
              {materialData[m.key]?.total_weight_kg ? `, ${materialData[m.key].total_weight_kg}` : ""}
            </li>
          ))}
        </ul>
        {totalWeightAuto > 0 && <div className="small text-muted mb-2">Berechnetes Gesamtgewicht (Schätzung): ~{totalWeightAuto} kg</div>}
        <div>
          <strong>Verpackung:</strong> {packaging.join(", ") || "—"}
        </div>
        <div>
          <strong>Beladung / Entladung:</strong> {loadingServiceAnswer || "—"} {helpers ? `(${helpers})` : ""}
        </div>
      </Section>

      <StickyActionBar
        onBack={onEditRoute}
        onSubmit={handleSubmit}
        disabled={!canSubmit}
        disabledHint={!canSubmit ? "Bitte beantworten Sie zuerst alle Fragen oben." : null}
      />
    </Card>
  );
};

/* ----------------------------------------------------------------------- */
/* TIRES (Reifen) — one continuous page                                    */
/* ----------------------------------------------------------------------- */

const TiresWizard = ({ onComplete, onEditRoute, initialData }) => {
  const { t } = useTranslation();
  const [selectedTypes, setSelectedTypes] = useState(() => initialData?.tires?.map((t) => t.item) || []);
  const [tireData, setTireData] = useState(() => dictByKey(initialData?.tires));
  const [location, setLocation] = useState(() => initialData?.location?.location_type || "");
  const [floor, setFloor] = useState(() => initialData?.location?.floor || "");
  const [hasElevator, setHasElevator] = useState(() => initialData?.location?.elevator || "");
  const [loadingHelp, setLoadingHelp] = useState(() => initialData?.loading_help || "");
  const [images, setImages] = useState([]);
  const [notes, setNotes] = useState(() => initialData?.notes || "");
  const [error, setError] = useState("");

  const setField = (type, field, value) => setTireData((prev) => ({ ...prev, [type]: { ...(prev[type] || {}), item: type, [field]: value } }));

  const activeTypes = selectedTypes.filter((t) => Number(tireData[t]?.quantity) > 0);
  const needsFloorQuestion = location === "Keller" || location === "Lager";

  const buildPayload = () => ({
    move_type: "Reifen",
    tires: activeTypes.map((t) => tireData[t]),
    location: { location_type: location, floor: needsFloorQuestion ? floor : null, elevator: needsFloorQuestion ? hasElevator : null },
    loading_help: loadingHelp,
    notes: notes || null,
  });

  const validate = () => {
    if (selectedTypes.length === 0) return "Bitte wählen Sie mindestens eine Reifenart aus.";
    if (activeTypes.length === 0) return "Bitte geben Sie die Anzahl der Reifen an.";
    if (!location) return "Bitte geben Sie an, wo sich die Reifen befinden.";
    if (!loadingHelp) return "Bitte beantworten Sie die Frage zur Be-/Entladehilfe.";
    return null;
  };

  const canSubmit = selectedTypes.length > 0 && activeTypes.length > 0 && !!location && !!loadingHelp;

  const handleSubmit = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    onComplete(buildPayload(), images);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Card className="p-3 mb-4">
      {error && (
        <Alert variant="danger" className="d-flex align-items-center gap-2">
          <FaExclamationTriangle /> {error}
        </Alert>
      )}

      <Section show first>
        <StepHeader title="Welche Reifen möchten Sie transportieren?" />
        <MultiSelect name="tire-types" options={TIRE_TYPES} values={selectedTypes} onChange={setSelectedTypes} />
      </Section>

      <Section show={selectedTypes.length > 0}>
        <SectionDivider />
        <StepHeader title="Anzahl der Reifen" />
        {selectedTypes.map((t) => (
          <Row key={t} className="align-items-center mb-2 pb-2 border-bottom">
            <Col xs={7}>{t}</Col>
            <Col xs={5}>
              <Counter value={tireData[t]?.quantity} onChange={(v) => setField(t, "quantity", v)} />
            </Col>
          </Row>
        ))}
      </Section>

      <Section show={activeTypes.length > 0}>
        <SectionDivider />
        <StepHeader title="Sind die Reifen auf Felgen montiert?" />
        {activeTypes.map((t) => (
          <div key={t} className="mb-3 pb-2 border-bottom">
            <div className="fw-semibold">{t}</div>
            <SingleSelect name={`${t}-rims`} options={ON_RIMS_OPTIONS} value={tireData[t]?.on_rims} onChange={(v) => setField(t, "on_rims", v)} />
            {tireData[t]?.on_rims === "Teilweise" && (
              <>
                <div className="small text-muted mb-1">Wie viele Reifen sind auf Felgen montiert?</div>
                <Counter value={tireData[t]?.quantity_on_rims} max={tireData[t]?.quantity} onChange={(v) => setField(t, "quantity_on_rims", v)} />
              </>
            )}
          </div>
        ))}
      </Section>

      <Section show={activeTypes.length > 0 && activeTypes.every((t) => !!tireData[t]?.on_rims)}>
        <SectionDivider />
        <StepHeader title="Reifengröße" />
        {activeTypes.map((t) => (
          <div key={t} className="mb-3 pb-2 border-bottom">
            <div className="fw-semibold">{t}</div>
            <Form.Control
              placeholder="z. B. 205/55 R16"
              value={tireData[t]?.size || ""}
              onChange={(e) => setField(t, "size", e.target.value)}
              disabled={tireData[t]?.size_unknown}
            />
            <Form.Check
              className="mt-1"
              type="checkbox"
              id={`${t}-size-unknown`}
              label="Reifengröße nicht bekannt"
              checked={!!tireData[t]?.size_unknown}
              onChange={(e) => setField(t, "size_unknown", e.target.checked)}
            />
          </div>
        ))}
      </Section>

      <Section show={activeTypes.length > 0 && activeTypes.every((t) => !!tireData[t]?.on_rims)}>
        <SectionDivider />
        <StepHeader title="Wo befinden sich die Reifen?" />
        <SingleSelect name="tire-location" options={TIRE_LOCATIONS} value={location} onChange={setLocation} />
        {needsFloorQuestion && (
          <>
            <Form.Group className="mt-2 mb-2">
              <Form.Label className="small text-muted">In welchem Stockwerk?</Form.Label>
              <Form.Control value={floor} onChange={(e) => setFloor(e.target.value)} />
            </Form.Group>
            <div className="small text-muted mb-1">Ist ein Aufzug vorhanden?</div>
            <SingleSelect name="tire-elevator" options={[t("Ja"), t("Nein"), t("Nicht bekannt")]} value={hasElevator} onChange={setHasElevator} />
          </>
        )}
      </Section>

      <Section show={!!location}>
        <SectionDivider />
        <StepHeader title="Benötigen Sie Hilfe beim Be- und Entladen?" />
        <SingleSelect name="loading-help" options={LOADING_HELP_OPTIONS} value={loadingHelp} onChange={setLoadingHelp} />
      </Section>

      <Section show={!!loadingHelp}>
        <SectionDivider />
        <StepHeader title="Fotos & Hinweise" subtitle="Optional" />
        <Card className="p-3 p-md-4 mb-4 border-0 rounded-3 bg-light">
          <h5 className="fw-bold mb-3">{t("dynamic_form.photos")}</h5>
          <Form.Group className="mb-3">
            <Form.Label>{t("dynamic_form.add_photos")}</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setImages(Array.from(e.target.files || []))}
            />
            <Form.Text className="text-muted">
              {t("dynamic_form.photos_hint")}
            </Form.Text>
          </Form.Group>

          {images.length > 0 && (
            <div className="mt-3">
              <h6>{t("dynamic_form.selected_photos")}</h6>
              <Row>
                {images.map((image, index) => (
                  <Col
                    xs={6}
                    sm={4}
                    md={3}
                    key={index}
                    className="mb-2 position-relative"
                  >
                    {image instanceof File ? (
                      <img
                        src={URL.createObjectURL(image)}
                        alt={t("dynamic_form.upload_alt", { index: index + 1 })}
                        style={{
                          width: "100%",
                          height: "80px",
                          objectFit: "cover",
                        }}
                        className="rounded"
                      />
                    ) : (
                      <img
                        src={image}
                        alt={t("dynamic_form.upload_alt", { index: index + 1 })}
                        style={{
                          width: "100%",
                          height: "80px",
                          objectFit: "cover",
                        }}
                        className="rounded"
                        onError={(e) => {
                          console.error("Error loading image:", image);
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0"
                      onClick={() => removeImage(index)}
                      style={{ transform: "translate(50%, -50%)" }}
                    >
                      ×
                    </Button>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Card>

        <Card className="p-3 p-md-4 mb-4 border-0 rounded-3 bg-light">
          <h5 className="fw-bold mb-3">{t("dynamic_form.additional_notes")}</h5>
          <Form.Group>
            <Form.Label>{t("dynamic_form.description")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder={t("dynamic_form.description_placeholder")}
              value={notes} 
              onChange={(e) => setNotes(e.target.value)}
            />
          </Form.Group>
        </Card>

        <SectionDivider />
        <StepHeader title="Ihre Auswahl" />
        <ul>
          {activeTypes.map((t) => (
            <li key={t}>
              {tireData[t].quantity} × {t}
              {tireData[t].on_rims ? `, auf Felgen: ${tireData[t].on_rims}` : ""}
              {tireData[t].size ? `, Größe: ${tireData[t].size}` : ""}
            </li>
          ))}
        </ul>
        <div>Standort: {location || "—"}</div>
        <div>Be-/Entladehilfe: {loadingHelp || "—"}</div>
      </Section>

      <StickyActionBar
        onBack={onEditRoute}
        onSubmit={handleSubmit}
        disabled={!canSubmit}
        disabledHint={!canSubmit ? "Bitte beantworten Sie zuerst alle Fragen oben." : null}
      />
    </Card>
  );
};

/* ----------------------------------------------------------------------- */
/* Deriving the shared/legacy fields (item_type, item_weight, load_helping,*/
/* loading_floor, item_breakable, special_treatment, ...) from the rich    */
/* per-type special_items payload.                                         */
/* ----------------------------------------------------------------------- */

const FRAGILE_FURNITURE_ITEMS = ["Aquarium", "Kunstwerk / großes Bild"];

const deriveLegacyFields = (type, payload) => {
  if (type === "furniture") {
    const heavyList = payload.heavy_items || [];
    const fragileItems = heavyList.filter((i) => FRAGILE_FURNITURE_ITEMS.includes(i.item));

    const items = [
      ...payload.items.map((i) => ({ item_type: i.item, num_items: i.quantity })),
      ...heavyList.map((i) => {
        const w = bucketToKg(i.size);
        return { item_type: i.item, num_items: i.quantity, ...(w ? { item_weight: w } : {}) };
      }),
    ];

    return {
      items,
      fragile: fragileItems.length > 0 ? "1" : "0",
      fragileDetails: fragileItems.map((i) => `${i.quantity}× ${i.item}${i.size ? ` (${i.size})` : ""}`).join(", ") || null,
      specialHandling: heavyList.length > 0 ? "1" : "0",
      specialHandlingDetails: heavyList.map((i) => `${i.quantity}× ${i.item}${i.size ? ` (${i.size})` : ""}`).join(", ") || null,
      helpCarry: payload.help_carry || "0",
      beladungFloor: payload.pickup_floor || null,
      entladungFloor: payload.delivery_floor || null,
      liftBeladung: !!payload.pickup_elevator,
      liftEntladung: !!payload.delivery_elevator,
    };
  }

  if (type === "building materials") {
    const materials = payload.materials || [];
    const glazedItems = materials.filter((m) => m.glazed === "Ja" || m.glazed === "Teilweise");
    const needsSpecial = payload.manual_review_required || (payload.loading_service?.equipment || []).some((e) => EQUIPMENT_NEEDS_PROVIDER.includes(e));

    const items = materials.map((m) => {
      const w = bucketToKg(m.total_weight_kg) || bucketToKg(m.weight_per_unit_kg);
      return { item_type: m.item, num_items: m.quantity || 1, ...(w ? { item_weight: w } : {}) };
    });

    const specialReasons = [];
    if (payload.total_weight_estimate === "Über 3,5 Tonnen") specialReasons.push("Gesamtgewicht über 3,5 Tonnen");
    if ((payload.packaging || []).includes("Lose")) specialReasons.push("Material unverpackt/lose");
    if ((payload.loading_service?.equipment || []).some((e) => EQUIPMENT_NEEDS_PROVIDER.includes(e))) {
      specialReasons.push(`Benötigtes Gerät: ${payload.loading_service.equipment.filter((e) => EQUIPMENT_NEEDS_PROVIDER.includes(e)).join(", ")}`);
    }

    return {
      items,
      fragile: glazedItems.length > 0 ? "1" : "0",
      fragileDetails: glazedItems.map((m) => m.item).join(", ") || null,
      specialHandling: needsSpecial ? "1" : "0",
      specialHandlingDetails: specialReasons.join("; ") || null,
      helpCarry: mapServiceToHelpCarry(payload.loading_service?.service),
      beladungFloor: payload.pickup?.floor || null,
      entladungFloor: payload.delivery?.floor || null,
      liftBeladung: payload.pickup?.freight_elevator === "Ja",
      liftEntladung: payload.delivery?.freight_elevator === "Ja",
    };
  }

  if (type === "tires") {
    const tires = payload.tires || [];
    const hasTruckTires = tires.some((t) => t.item === "LKW-Reifen" && Number(t.quantity) > 0);

    return {
      items: tires.map((t) => ({ item_type: t.item, num_items: t.quantity })),
      fragile: "0",
      fragileDetails: null,
      specialHandling: hasTruckTires ? "1" : "0",
      specialHandlingDetails: hasTruckTires ? "LKW-Reifen ausgewählt – ggf. Sondertransport erforderlich" : null,
      helpCarry: mapServiceToHelpCarry(payload.loading_help),
      beladungFloor: payload.location?.floor || null,
      entladungFloor: null,
      liftBeladung: payload.location?.elevator === "Ja",
      liftEntladung: false,
    };
  }

  if (type === "moving") {
    const heavyList = payload.heavy_items || [];
    const fragileItems = heavyList.filter((i) => FRAGILE_FURNITURE_ITEMS.includes(i.item));

    const items = [
      ...payload.items.map((i) => ({ item_type: i.item, num_items: i.quantity })),
      ...heavyList.map((i) => {
        const w = bucketToKg(i.size);
        return { item_type: i.item, num_items: i.quantity, ...(w ? { item_weight: w } : {}) };
      }),
    ];

    const specialReasons = [];
    if (heavyList.length > 0) specialReasons.push(heavyList.map((i) => `${i.quantity}× ${i.item}${i.size ? ` (${i.size})` : ""}`).join(", "));
    if (payload.special_transport_required) specialReasons.push("Motorrad ausgewählt – Sondertransport erforderlich");

    return {
      items,
      fragile: fragileItems.length > 0 ? "1" : "0",
      fragileDetails: fragileItems.map((i) => `${i.quantity}× ${i.item}${i.size ? ` (${i.size})` : ""}`).join(", ") || null,
      specialHandling: heavyList.length > 0 || payload.special_transport_required ? "1" : "0",
      specialHandlingDetails: specialReasons.join("; ") || null,
      helpCarry: payload.help_carry || "0",
      beladungFloor: payload.pickup_floor || null,
      entladungFloor: payload.delivery_floor || null,
      liftBeladung: !!payload.pickup_elevator,
      liftEntladung: !!payload.delivery_elevator,
    };
  }

  return { items: [] };
};

/* ----------------------------------------------------------------------- */
/* Entry point                                                             */
/* ----------------------------------------------------------------------- */

const SpecialItemsWizard = ({ type, onNext, onEditRoute, initialItems }) => {
  const handleComplete = (payload, images) => {
    const legacy = deriveLegacyFields(type, payload);
    onNext({
      items: legacy.items,
      special_items: payload,
      images: images || [],
      description: payload.notes || null,
      fragile: legacy.fragile,
      fragileDetails: legacy.fragileDetails,
      specialHandling: legacy.specialHandling,
      specialHandlingDetails: legacy.specialHandlingDetails,
      helpCarry: legacy.helpCarry,
      beladungFloor: legacy.beladungFloor,
      entladungFloor: legacy.entladungFloor,
      liftBeladung: legacy.liftBeladung,
      liftEntladung: legacy.liftEntladung,
    });
  };

  if (type === "furniture") {
    return <FurnitureWizard onComplete={handleComplete} onEditRoute={onEditRoute} initialData={initialItems?.special_items || initialItems} />;
  }
  if (type === "building materials") {
    return <BuildingMaterialsWizard onComplete={handleComplete} onEditRoute={onEditRoute} initialData={initialItems?.special_items || initialItems} />;
  }
  if (type === "tires") {
    return <TiresWizard onComplete={handleComplete} onEditRoute={onEditRoute} initialData={initialItems?.special_items || initialItems} />;
  }
  if (type === "moving") {
    return <MoveWizard onComplete={handleComplete} onEditRoute={onEditRoute} initialData={initialItems?.special_items || initialItems} />;
  }
  return null;
};

export default SpecialItemsWizard;